import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';

/**
 * Adaptive density (PRODUCT.md: "roomy and approachable by default, with a
 * compact mode for power users").
 *
 * It is one attribute on `<html>` and three CSS variables -- `--dy-row-py`,
 * `--dy-row-px`, `--dy-gap` -- so a component never asks what density it is in.
 * That is deliberate: the moment a component branches on density there are two
 * layouts to keep honest instead of one.
 *
 * Stored under its own key rather than next-themes' so the two preferences are
 * independent; a person who likes dark does not have to like compact.
 */
export type Density = 'roomy' | 'compact';

const KEY = 'dyspatch.density';

interface DensityValue {
  density: Density;
  setDensity: (next: Density) => void;
  toggleDensity: () => void;
}

const DensityContext = createContext<DensityValue | null>(null);

function stored(): Density {
  try {
    return localStorage.getItem(KEY) === 'compact' ? 'compact' : 'roomy';
  } catch {
    return 'roomy';
  }
}

export function DensityProvider({ children }: { children: ReactNode }) {
  const [density, setDensityState] = useState<Density>(stored);

  // A layout effect, not an effect: the attribute is on the document before the
  // browser paints, so a compact user never sees a roomy first frame.
  useLayoutEffect(() => {
    document.documentElement.dataset.density = density;
  }, [density]);

  const setDensity = useCallback((next: Density) => {
    setDensityState(next);
    try {
      localStorage.setItem(KEY, next);
    } catch {
      // A browser with storage denied still gets the switch, just not the memory.
    }
  }, []);

  const toggleDensity = useCallback(() => {
    setDensityState((previous) => {
      const next = previous === 'compact' ? 'roomy' : 'compact';
      try {
        localStorage.setItem(KEY, next);
      } catch {
        // as above
      }
      return next;
    });
  }, []);

  return (
    <DensityContext.Provider value={{ density, setDensity, toggleDensity }}>
      {children}
    </DensityContext.Provider>
  );
}

export function useDensity(): DensityValue {
  const value = useContext(DensityContext);
  if (!value) throw new Error('useDensity used outside <DensityProvider>');
  return value;
}
